import { useEffect, useMemo, useState } from 'react';
import {
  CalendarRange,
  Loader2,
  TrendingUp,
  X,
  Filter,
  Tag,
  Building2,
  Moon,
  Users,
  Euro,
  Clock,
  XCircle,
  UserCheck,
  Repeat,
  BedDouble,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { AnalyticsFiltersData, HistoricoReservasResult, ModoAlicante } from '@/lib/types';
import { ZONE_LABELS } from '@/lib/utils';
import { yearRange } from '@/lib/analyticsDates';
import MultiSelect from '@/components/ui/MultiSelect';
import PanelFiltros from '@/components/analytics/PanelFiltros';
import FiltroAlicante from '@/components/analytics/FiltroAlicante';
import { KPI, fmtMoney, fmtNum, fmtPct, fmtDia } from '@/components/analytics/historico/comun';
import SeccionEvolucion from '@/components/analytics/historico/SeccionEvolucion';
import SeccionEstacionalidad from '@/components/analytics/historico/SeccionEstacionalidad';
import SeccionComportamiento from '@/components/analytics/historico/SeccionComportamiento';
import SeccionCanales from '@/components/analytics/historico/SeccionCanales';
import SeccionHuespedes from '@/components/analytics/historico/SeccionHuespedes';
import SeccionPropiedades from '@/components/analytics/historico/SeccionPropiedades';

// Hay reservas desde 2019 y confirmadas para el año que viene.
const ANIO_MIN = 2019;

const INFO = {
  reservas: 'Reservas en estado «accepted» en Hostify con entrada dentro del rango elegido (por defecto, todo el histórico). Quedan fuera cancelaciones, consultas y solicitudes no confirmadas. Debajo, los anuncios de Hostify con alguna reserva: más que alojamientos, porque uno puede tener varios anuncios y los que no están dados de alta en el CRM cuentan sueltos.',
  noches: 'Suma de las noches de esas reservas, enteras: aquí no se prorratea nada por periodo.',
  huespedes: 'Suma del campo guests de cada reserva confirmada.',
  facturacion: 'Alojamiento + limpieza + extras de las reservas confirmadas, con IVA en todos los canales (el de Booking, que Hostify trae aparte, se suma). Importe bruto, sin descontar comisiones. Misma definición que en Finance.',
  neto: 'Subtotal de Hostify menos comisión del canal, tarifa de transacción y reembolsos: el «Total Revenue» de sus informes.',
  adr: 'Ingresos de alojamiento (sin limpieza ni extras) entre noches vendidas.',
  estancia: 'Noches entre reservas confirmadas.',
  grupo: 'Huéspedes entre reservas confirmadas.',
  antelacion: 'Días entre la fecha de la reserva y la entrada (advance_days). La mediana resiste mejor a las reservas hechas con un año de antelación.',
  cancelacion: 'Reservas canceladas entre confirmadas más canceladas. Una reserva cancelada en Hostify deja de estar «accepted», así que no se cuenta dos veces.',
  clientes: 'Personas distintas: los perfiles de huésped que comparten teléfono o email se agrupan como un solo cliente.',
  repetidores: 'Parte de las reservas confirmadas que son de clientes con dos o más reservas en el histórico filtrado.',
};

type SeleccionAnio = 'todo' | 'custom' | number;

function derivarAnio(from: string, to: string): SeleccionAnio {
  if (!from && !to) return 'todo';
  const y = Number(from.substring(0, 4));
  if (y && from === yearRange(y).from && to === yearRange(y).to) return y;
  return 'custom';
}

export default function ReservasHistoricoPage() {
  const [filtersData, setFiltersData] = useState<AnalyticsFiltersData | null>(null);
  const [data, setData] = useState<HistoricoReservasResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // El año en curso por defecto: es lo que se mira a diario. «Todo el
  // histórico» sigue a un clic en el selector de periodo.
  const inicial = yearRange(new Date().getFullYear());
  const [from, setFrom] = useState(inicial.from);
  const [to, setTo] = useState(inicial.to);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [zones, setZones] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [includeOwner, setIncludeOwner] = useState(false);
  // Los mismos valores de partida que Finance, para que las dos pantallas
  // cuenten lo mismo mientras no se toque nada. Las propiedades que ya no se
  // gestionan siguen a un clic: desmarcar la casilla las devuelve al histórico.
  const [activeOnly, setActiveOnly] = useState(true);
  const [includeFamily, setIncludeFamily] = useState(true);
  // Alicante fuera por defecto: es otra operativa y mezclarla distorsiona las
  // medias. Como «solo activas», cuenta como filtro al SALIRSE de ese estado.
  const [alicante, setAlicante] = useState<ModoAlicante>('sin');

  useEffect(() => {
    api.getAnalyticsFilters().then(setFiltersData).catch(() => setFiltersData(null));
  }, []);

  const cargar = async (overrides?: { from?: string; to?: string; propertyIds?: string[]; includeFamily?: boolean; alicante?: ModoAlicante }) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getHistoricoReservas({
        from: overrides?.from ?? from,
        to: overrides?.to ?? to,
        propertyIds: overrides?.propertyIds ?? propertyIds,
        zones,
        channels,
        includeOwner,
        activeOnly,
        excludeFamily: !(overrides?.includeFamily ?? includeFamily),
        alicante: overrides?.alicante ?? alicante,
      });
      setData(result);
    } catch (e: any) {
      console.error(e);
      setError(e?.message || 'No se pudo cargar el histórico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const propertyOptions = useMemo(() => {
    const list = filtersData?.properties || [];
    let visible = activeOnly ? list.filter((p) => p.active) : list;
    if (!includeFamily) visible = visible.filter((p) => !p.family);
    if (alicante === 'sin') visible = visible.filter((p) => !p.alicante);
    if (alicante === 'solo') visible = visible.filter((p) => p.alicante);
    return visible.map((p) => ({ value: p.id, label: p.name }));
  }, [filtersData, activeOnly, includeFamily, alicante]);
  const zoneOptions = useMemo(() => (filtersData?.zones || []).map((z) => ({ value: z, label: ZONE_LABELS[z] || z })), [filtersData]);
  // Direct, Website y Tu Sereno son un solo canal en esta pantalla (el servidor
  // los junta al agregar y al filtrar), así que en el selector aparecen una vez.
  const channelOptions = useMemo(() => {
    const canales = new Set((filtersData?.channels || []).map((c) => (/^\s*(direct|website|tu\s*sereno)\s*$/i.test(c) ? 'Tu Sereno' : c)));
    return [...canales].sort((a, b) => a.localeCompare(b)).map((c) => ({ value: c, label: c }));
  }, [filtersData]);

  const anioMax = new Date().getFullYear() + 1;
  const anios: number[] = [];
  for (let y = ANIO_MIN; y <= anioMax; y++) anios.push(y);
  const seleccion = derivarAnio(from, to);

  const elegirAnio = (valor: string) => {
    const r = valor === 'todo' ? { from: '', to: '' } : yearRange(Number(valor));
    setFrom(r.from);
    setTo(r.to);
    cargar(r);
  };

  const activos =
    propertyIds.length +
    zones.length +
    channels.length +
    (includeOwner ? 1 : 0) +
    (activeOnly ? 0 : 1) +
    (includeFamily ? 0 : 1) +
    (alicante === 'sin' ? 0 : 1);

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-sereno-blue/50 text-sereno-dark">
          <CalendarRange size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Reservas</h1>
          <p className="text-sm text-sereno-dark/50">Cómo se reserva, mirando todo el histórico de Hostify</p>
        </div>
      </div>

      <PanelFiltros
        activos={activos}
        barra={
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={seleccion === 'custom' ? 'custom' : String(seleccion)}
              onChange={(e) => elegirAnio(e.target.value)}
              className="input min-h-11 w-auto max-w-full"
              aria-label="Periodo"
            >
              {seleccion === 'custom' && (
                <option value="custom" disabled>
                  Personalizado
                </option>
              )}
              <option value="todo">Todo el histórico</option>
              {anios.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
            {data && (
              <span className="text-sm text-sereno-dark/50">
                {fmtNum(data.kpis.reservas)} reservas confirmadas
                {data.rango.primeraEntrada && (
                  <>
                    {' '}· entradas del {fmtDia(data.rango.primeraEntrada)} al {fmtDia(data.rango.ultimaEntrada)}
                  </>
                )}
                {' '}· {fmtNum(data.rango.documentos)} documentos en total
              </span>
            )}
          </div>
        }
      >
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Entradas desde</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input min-h-11" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-sereno-dark/60 mb-1">Entradas hasta</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input min-h-11" />
          </div>
          <MultiSelect label="Zonas" icon={Filter} options={zoneOptions} selected={zones} onChange={setZones} allLabel="Todas" />
          <MultiSelect label="Canales" icon={Tag} options={channelOptions} selected={channels} onChange={setChannels} allLabel="Todos" searchable />
          <MultiSelect label="Propiedades" icon={Building2} options={propertyOptions} selected={propertyIds} onChange={setPropertyIds} allLabel="Todas" searchable />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1">
          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input type="checkbox" checked={includeOwner} onChange={(e) => setIncludeOwner(e.target.checked)} className="rounded" />
            Incluir estancias Owner
          </label>
          <label className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => {
                const next = e.target.checked;
                setActiveOnly(next);
                if (next) setPropertyIds((ids) => ids.filter((id) => filtersData?.properties.find((p) => p.id === id)?.active));
              }}
              className="rounded"
            />
            Solo propiedades activas (PMS)
          </label>
          <label
            className="min-h-11 flex items-center gap-2 text-sm text-sereno-dark/70 cursor-pointer"
            title="Malia Bernal (3) y CasaML (2). Al desactivarlas salen de todos los cálculos."
          >
            <input
              type="checkbox"
              checked={includeFamily}
              onChange={(e) => {
                const next = e.target.checked;
                setIncludeFamily(next);
                const nextIds = next ? propertyIds : propertyIds.filter((id) => !filtersData?.properties.find((p) => p.id === id)?.family);
                setPropertyIds(nextIds);
                cargar({ includeFamily: next, propertyIds: nextIds });
              }}
              className="rounded"
            />
            Incluir propiedades familiares
          </label>

          <FiltroAlicante
            valor={alicante}
            onChange={(next) => {
              setAlicante(next);
              const nextIds = propertyIds.filter((id) => {
                const p = filtersData?.properties.find((x) => x.id === id);
                if (!p) return true;
                return next === 'sin' ? !p.alicante : next === 'solo' ? p.alicante : true;
              });
              setPropertyIds(nextIds);
              cargar({ alicante: next, propertyIds: nextIds });
            }}
          />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <button onClick={() => cargar()} disabled={loading} className="btn-primary min-h-11 flex items-center gap-2">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <TrendingUp size={16} />}
            Calcular
          </button>
          {activos > 0 && (
            <button
              onClick={() => {
                setPropertyIds([]);
                setZones([]);
                setChannels([]);
                setIncludeOwner(false);
                setActiveOnly(true);
                setIncludeFamily(true);
                setAlicante('sin');
              }}
              className="min-h-11 flex items-center gap-1.5 text-sm text-sereno-dark/50 hover:text-sereno-dark"
            >
              <X size={14} /> Limpiar filtros ({activos})
            </button>
          )}
        </div>
      </PanelFiltros>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-sereno-dark/40" />
        </div>
      ) : error && !data ? (
        <div className="card text-center py-12 text-rose-600">{error}</div>
      ) : !data ? (
        <div className="card text-center py-12 text-sereno-dark/50">No hay datos para los filtros seleccionados.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-6">
            <KPI icon={CalendarRange} label="Reservas" value={fmtNum(data.kpis.reservas)} sub={`${fmtNum(data.kpis.propiedades)} listings de Hostify`} info={INFO.reservas} color="bg-blue-50 text-blue-600" />
            <KPI icon={Moon} label="Noches" value={fmtNum(data.kpis.noches)} info={INFO.noches} color="bg-indigo-50 text-indigo-600" />
            <KPI icon={Users} label="Huéspedes" value={fmtNum(data.kpis.huespedes)} sub={`${fmtNum(data.kpis.huespedesMedia, 1)} por reserva`} info={INFO.huespedes} color="bg-purple-50 text-purple-600" />
            <KPI icon={Euro} label="Facturación" value={fmtMoney(data.kpis.facturacion)} info={INFO.facturacion} color="bg-emerald-50 text-emerald-600" />
            <KPI icon={Euro} label="Ingresos netos (Hostify)" value={fmtMoney(data.kpis.neto)} info={INFO.neto} color="bg-teal-50 text-teal-600" />
            <KPI icon={TrendingUp} label="ADR (precio/noche)" value={fmtMoney(data.kpis.adr)} info={INFO.adr} color="bg-amber-50 text-amber-600" />
            <KPI icon={BedDouble} label="Estancia media" value={`${fmtNum(data.kpis.estanciaMedia, 1)} noches`} info={INFO.estancia} color="bg-sky-50 text-sky-600" />
            <KPI icon={Clock} label="Antelación media" value={`${fmtNum(data.kpis.antelacionMedia)} días`} sub={`mediana ${fmtNum(data.kpis.antelacionMediana)} días`} info={INFO.antelacion} color="bg-orange-50 text-orange-600" />
            <KPI icon={XCircle} label="Tasa de cancelación" value={fmtPct(data.kpis.tasaCancelacion, 1)} sub={`${fmtNum(data.kpis.canceladas)} canceladas`} info={INFO.cancelacion} color="bg-rose-50 text-rose-600" />
            <KPI icon={UserCheck} label="Clientes distintos" value={fmtNum(data.kpis.huespedesUnicos)} sub={`${fmtNum(data.kpis.repetidores)} repiten`} info={INFO.clientes} color="bg-lime-50 text-lime-600" />
            <KPI icon={Repeat} label="Reservas de repetidores" value={fmtPct(data.kpis.pctReservasRepetidores, 1)} sub={`${fmtNum(data.kpis.reservasRepetidores)} reservas`} info={INFO.repetidores} color="bg-cyan-50 text-cyan-600" />
            <KPI icon={CalendarRange} label="Cartera futura" value={fmtNum(data.cartera.reservas)} sub={`${fmtNum(data.cartera.noches)} noches · ${fmtMoney(data.cartera.facturacion)}`} info="Reservas confirmadas con entrada posterior a hoy." color="bg-fuchsia-50 text-fuchsia-600" />
          </div>

          <SeccionEvolucion data={data} />
          <SeccionEstacionalidad data={data} />
          <SeccionComportamiento data={data} />
          <SeccionCanales data={data} />
          <SeccionHuespedes data={data} />
          <SeccionPropiedades data={data} />
        </>
      )}
    </div>
  );
}
